import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { BuildingInformationProps } from './types'
import {
  InfoSection,
  PropertySectionHeader as SectionHeader,
  PropertySectionTitle as SectionTitle,
  SectionIcon,
  SectionContent,
  PropertyDataGrid as DataGrid,
  PropertyDataRow as DataRow,
  DataValue,
  DataValueWithUnit,
  DataUnit,
} from '../../../molecules/PropertyPanelStyles'
import { DataLabelWithTooltip } from '../../../molecules/DataLabelWithTooltip'
import { Icon } from '../../../atoms/Icon'
import { formatNumberSwiss } from '../../../../utils/number/formatUtils'
import { translateGwrValue } from '../../../../utils/gwrTranslations'
import { ProtectedBuildingModal } from '../components/ProtectedBuildingModal'

const ClickableProtection = styled.span<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ $color }) => $color || 'inherit'};
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;

  &:hover {
    opacity: 0.8;
  }
`

const getProtectedRankColor = (rank?: number): string | undefined => {
  if (rank === undefined || rank === null || rank === 0) return undefined
  if (rank >= 4) return '#ff3b30'
  if (rank >= 3) return '#ff9500'
  if (rank >= 2) return '#ffcc00'
  return '#34c759'
}

export const BuildingInformation: React.FC<BuildingInformationProps> = ({
  building,
}) => {
  const { t } = useTranslation('explore')
  const { t: tCommon } = useTranslation('common')
  const [isProtectedModalOpen, setIsProtectedModalOpen] = useState(false)
  const hasProtectedInfo = !!building?.protected_information_json
  const protectionColor = getProtectedRankColor(building?.protected_rank)

  const formatYear = (
    year: number | string | undefined | null,
    period?: string
  ): string => {
    if (year) return year.toString()
    if (period) return period
    return '-'
  }

  const getProtectionText = (rank: number | undefined | null): string => {
    if (rank === null || rank === undefined) return '-'
    return rank > 1 ? t('common.yes') : t('common.no')
  }

  const getCategoryText = (category: string | undefined | null): string => {
    if (!category) return '-'
    return t(`buildingClassifications.${category}`, { defaultValue: category })
  }

  return (
    <InfoSection>
      <SectionHeader>
        <SectionTitle>
          <SectionIcon>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
            </svg>
          </SectionIcon>
          {t('propertyTooltip.buildingInformation.title')}
        </SectionTitle>
      </SectionHeader>
      <SectionContent>
        <DataGrid>
          <DataRow>
            <DataLabelWithTooltip
              label={t('common.landRadarInfo.buildingYear')}
              explanation={tCommon(
                'propertyExplanations.buildingInformation.buildingYear'
              )}
            >
              {t('common.landRadarInfo.buildingYear')}
            </DataLabelWithTooltip>
            <DataValue>
              {formatYear(building?.building_year, building?.building_period)}
            </DataValue>
          </DataRow>

          <DataRow>
            <DataLabelWithTooltip
              label={t('common.buildingTooltip.Année de rénovation')}
              explanation={tCommon(
                'propertyExplanations.buildingInformation.renovationYear'
              )}
            >
              {t('common.buildingTooltip.Année de rénovation')}
            </DataLabelWithTooltip>
            <DataValue>
              {formatYear(
                building?.renovation_year,
                building?.renovation_period
              )}
            </DataValue>
          </DataRow>

          <DataRow>
            <DataLabelWithTooltip
              label={t('common.buildingTooltip.Protection patrimoine')}
              explanation={tCommon(
                'propertyExplanations.buildingInformation.heritageProtection'
              )}
            >
              {t('common.buildingTooltip.Protection patrimoine')}
            </DataLabelWithTooltip>
            {hasProtectedInfo ? (
              <ClickableProtection
                $color={protectionColor}
                onClick={() => setIsProtectedModalOpen(true)}
              >
                {getProtectionText(building?.protected_rank)}
                <Icon name="info" size="small" />
              </ClickableProtection>
            ) : (
              <DataValue $color={protectionColor}>
                {getProtectionText(building?.protected_rank)}
              </DataValue>
            )}
          </DataRow>

          <DataRow>
            <DataLabelWithTooltip
              label={t('common.filterFieldLabels.popetyClassification')}
              explanation={tCommon(
                'propertyExplanations.buildingInformation.popetyClassification'
              )}
            >
              {t('common.filterFieldLabels.popetyClassification')}
            </DataLabelWithTooltip>
            <DataValue>
              {getCategoryText(building?.popety_classification) || '-'}
            </DataValue>
          </DataRow>

          <DataRow>
            <DataLabelWithTooltip
              label={t('common.filterFieldLabels.buildingClassGwr')}
              explanation={tCommon(
                'propertyExplanations.buildingInformation.buildingClassGwr'
              )}
            >
              {t('common.filterFieldLabels.buildingClassGwr')}
            </DataLabelWithTooltip>
            <DataValue>
              {translateGwrValue(building?.category, 'buildingClass', t)}
            </DataValue>
          </DataRow>

          <DataRow>
            <DataLabelWithTooltip
              label={t('common.filterFieldLabels.buildingCategoryGwr')}
              explanation={tCommon(
                'propertyExplanations.buildingInformation.buildingCategoryGwr'
              )}
            >
              {t('common.filterFieldLabels.buildingCategoryGwr')}
            </DataLabelWithTooltip>
            <DataValue>
              {translateGwrValue(building?.type, 'buildingCategory', t)}
            </DataValue>
          </DataRow>

          <DataRow>
            <DataLabelWithTooltip
              label={t('common.buildingTooltip.Surface au sol')}
              explanation={tCommon(
                'propertyExplanations.buildingInformation.groundFloorArea'
              )}
            >
              {t('common.buildingTooltip.Surface au sol')}
            </DataLabelWithTooltip>
            <DataValueWithUnit>
              <DataValue>{formatNumberSwiss(building?.area) || '-'}</DataValue>
              <DataUnit>m²</DataUnit>
            </DataValueWithUnit>
          </DataRow>

          <DataRow>
            <DataLabelWithTooltip
              label={t('common.info.grossFloor')}
              explanation={tCommon(
                'propertyExplanations.buildingInformation.grossFloorArea'
              )}
            >
              {t('common.info.grossFloor')}
            </DataLabelWithTooltip>
            <DataValueWithUnit>
              <DataValue>
                {formatNumberSwiss(building?.gross_floor_area) || '-'}
              </DataValue>
              <DataUnit>m²</DataUnit>
            </DataValueWithUnit>
          </DataRow>

          <DataRow>
            <DataLabelWithTooltip
              label={t('common.info.Volume')}
              explanation={tCommon(
                'propertyExplanations.buildingInformation.volume'
              )}
            >
              {t('common.info.Volume')}
            </DataLabelWithTooltip>
            <DataValueWithUnit>
              <DataValue>
                {formatNumberSwiss(
                  building?.volume_estimate ?? building?.building_volume
                ) || '-'}
              </DataValue>
              <DataUnit>m³</DataUnit>
            </DataValueWithUnit>
          </DataRow>

          <DataRow>
            <DataLabelWithTooltip
              label={t('common.buildingTooltip.Hauteur max')}
              explanation={tCommon(
                'propertyExplanations.buildingInformation.maxHeight'
              )}
            >
              {t('common.buildingTooltip.Hauteur max')}
            </DataLabelWithTooltip>
            <DataValueWithUnit>
              <DataValue>
                {formatNumberSwiss(
                  building?.height_estimate ?? building?.height
                ) || '-'}
              </DataValue>
              <DataUnit>m</DataUnit>
            </DataValueWithUnit>
          </DataRow>

          <DataRow>
            <DataLabelWithTooltip
              label={t('common.buildingTooltip.Nombre de niveaux')}
              explanation={tCommon(
                'propertyExplanations.buildingInformation.numberOfFloors'
              )}
            >
              {t('common.buildingTooltip.Nombre de niveaux')}
            </DataLabelWithTooltip>
            <DataValue>
              {formatNumberSwiss(building?.floor_nb) || '-'}
            </DataValue>
          </DataRow>
        </DataGrid>
      </SectionContent>

      {hasProtectedInfo && building.protected_information_json && (
        <ProtectedBuildingModal
          isOpen={isProtectedModalOpen}
          onClose={() => setIsProtectedModalOpen(false)}
          protectedInfo={building.protected_information_json}
        />
      )}
    </InfoSection>
  )
}

export default BuildingInformation
